/**
 * Settings-Karte fuer Notification-Ruhezeiten und Kanal-Overrides.
 *
 * Seit 2026-09-30 (Regel „Dokument-Navigation" E10, Abhakliste 1.46): Ruhezeit und
 * Kanäle sind ein Einstellungs-Abschnitt — zeigt zuerst an, „Bearbeiten" öffnet,
 * „Speichern" schreibt per PUT /api/notifications/settings, „Abbrechen" führt zurück
 * in die Anzeige. Der Web-Push-Schalter bleibt eine Einzel-Aktion je Gerät (Ausnahme
 * E4, Betriebssystem-Berechtigung) mit sichtbarer Rückmeldung.
 *
 * Der Backend-Endpoint liefert gleichzeitig die im Code hinterlegten Slug-Defaults +
 * Labels, sodass das Frontend keine eigene Slug-Liste hardcoden muss.
 *
 * Punkt D aus dem Todos-Notifications-Plan (2026-07-01).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, BellRing, Loader2, Lock, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { SettingsSection } from '@onvice/portal-ui';
import {
  canUsePush,
  isSubscribed as isPushSubscribed,
  subscribePush,
  unsubscribePush,
} from '../lib/pushNotifications';

type Channel = 'in_app' | 'push_web' | 'push_mobile' | 'email';

const CHANNEL_LABELS: Record<Channel, string> = {
  in_app: 'In-App',
  push_web: 'Web-Push',
  push_mobile: 'Mobile-Push',
  email: 'E-Mail',
};

// Nur die Channels, die der User heute tatsaechlich pflegen soll.
// push_mobile + email sind Platzhalter fuer Phase 5b/spaeter und werden
// versteckt bis die Backend-Zustellung dafuer da ist.
const VISIBLE_CHANNELS: Channel[] = ['in_app', 'push_web'];

interface SettingsResponse {
  success: boolean;
  settings: {
    quiet_hours_start: string | null;
    quiet_hours_end: string | null;
    quiet_weekends: boolean;
    quiet_holidays: boolean;
    channel_overrides: Record<string, Channel[]>;
  };
  slug_defaults: Record<string, Channel[]>;
  slug_labels: Record<string, string>;
  mandatory_slugs: string[];
  is_gf_or_admin: boolean;
}

interface Meta {
  defaults: Record<string, Channel[]>;
  labels: Record<string, string>;
  mandatorySlugs: string[];
  isGfOrAdmin: boolean;
}

interface Prefs {
  quietStart: string;
  quietEnd: string;
  weekends: boolean;
  holidays: boolean;
  overrides: Record<string, Channel[]>;
}

export default function NotificationSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [meta, setMeta] = useState<Meta>({ defaults: {}, labels: {}, mandatorySlugs: [], isGfOrAdmin: false });
  const [pushState, setPushState] = useState<'unknown' | 'unsupported' | 'off' | 'on' | 'denied'>('unknown');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState('');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get<SettingsResponse>('/notifications/settings');
      setPrefs({
        quietStart: r.settings.quiet_hours_start ?? '',
        quietEnd: r.settings.quiet_hours_end ?? '',
        weekends: r.settings.quiet_weekends,
        holidays: r.settings.quiet_holidays,
        overrides: r.settings.channel_overrides ?? {},
      });
      setMeta({
        defaults: r.slug_defaults ?? {},
        labels: r.slug_labels ?? {},
        mandatorySlugs: r.mandatory_slugs ?? [],
        isGfOrAdmin: r.is_gf_or_admin ?? false,
      });
    } catch (err) {
      setLoadError((err as Error).message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const refreshPushState = useCallback(async (): Promise<void> => {
    if (!canUsePush) { setPushState('unsupported'); return; }
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      setPushState('denied'); return;
    }
    const sub = await isPushSubscribed();
    setPushState(sub ? 'on' : 'off');
  }, []);

  useEffect(() => { void refreshPushState(); }, [refreshPushState]);

  const togglePush = async (): Promise<void> => {
    setPushBusy(true); setPushMessage('');
    try {
      if (pushState === 'on') {
        await unsubscribePush();
        // Prompt-Dismiss-Flag zuruecksetzen, damit der Login-Banner
        // beim naechsten Login wieder erscheint.
        try { localStorage.removeItem('push-prompt-dismissed'); } catch { /* ignore */ }
        setPushMessage('Web-Push deaktiviert.');
      } else {
        const r = await subscribePush();
        if (!r.ok) {
          setPushMessage(r.reason ?? 'Aktivierung fehlgeschlagen');
        } else {
          try { localStorage.removeItem('push-prompt-dismissed'); } catch { /* ignore */ }
          setPushMessage('Web-Push aktiviert.');
        }
      }
      await refreshPushState();
    } catch (e) {
      setPushMessage(`Fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setPushBusy(false);
    }
  };

  const save = useCallback(async (value: Prefs): Promise<Prefs> => {
    await api.put('/notifications/settings', {
      quiet_hours_start: value.quietStart || null,
      quiet_hours_end: value.quietEnd || null,
      quiet_weekends: value.weekends,
      quiet_holidays: value.holidays,
      channel_overrides: value.overrides,
    });
    return value;
  }, []);

  return (
    <div
      data-test-anchor="benachrichtigungen"
      className="bg-card border border-default rounded-xl p-4 sm:p-5 mb-4"
    >
      <div className="flex items-center gap-2 mb-4">
        <Bell size={18} className="text-primary" />
        <h2 className="text-lg font-semibold">Benachrichtigungen</h2>
      </div>

      <section data-test-anchor="web-push-toggle" className="mb-5">
        <h3 className="text-sm font-semibold mb-2">Web-Push</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Bekommt dieses Gerät Betriebssystem-Benachrichtigungen (z.B. bei
          Compliance-Warnungen, Todos oder neuen Chats). Muss pro Gerät
          einmal aktiviert werden — gilt sofort.
        </p>
        {pushState === 'unsupported' && (
          <div className="text-xs text-muted-foreground">Dieser Browser unterstützt Web-Push nicht.</div>
        )}
        {pushState === 'denied' && (
          <div className="text-xs text-rose-500">
            Benachrichtigungen sind für diese Seite in den Browser-Einstellungen blockiert.
            Bitte dort erst wieder erlauben, dann hier aktivieren.
          </div>
        )}
        {(pushState === 'off' || pushState === 'on' || pushState === 'unknown') && (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void togglePush()}
              disabled={pushBusy || pushState === 'unknown'}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border min-h-[44px] ${
                pushState === 'on'
                  ? 'bg-emerald-600 text-white border-emerald-500 hover:bg-emerald-500'
                  : 'bg-background border-default hover:border-primary'
              } disabled:opacity-50`}
            >
              {pushBusy
                ? <Loader2 size={14} className="animate-spin" />
                : <BellRing size={14} />}
              {pushState === 'on' ? 'Aktiv — deaktivieren' : 'Web-Push aktivieren'}
            </button>
            {pushState === 'on' && (
              <button
                type="button"
                onClick={async () => {
                  setPushBusy(true); setPushMessage('');
                  try {
                    await api.post('/notifications/push-test');
                    setPushMessage('Test-Push gesendet — sollte gleich ankommen.');
                  } catch (e) {
                    setPushMessage(`Test fehlgeschlagen: ${(e as Error).message}`);
                  } finally { setPushBusy(false); }
                }}
                disabled={pushBusy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-default bg-background hover:border-primary disabled:opacity-50 min-h-[44px]"
              >
                <Send size={14} /> Test-Push an mich
              </button>
            )}
            {pushMessage && <span className="text-xs text-muted-foreground" data-test-anchor="web-push-meldung">{pushMessage}</span>}
          </div>
        )}
      </section>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-3">
          <Loader2 size={14} className="animate-spin" /> Lade Einstellungen…
        </div>
      ) : loadError ? (
        <div className="rounded-md bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-rose-400 flex flex-wrap items-center gap-3" data-test-anchor="benachrichtigungen-ladefehler">
          <span>Nicht geladen: {loadError}</span>
          <button type="button" onClick={() => void load()} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-xs hover:bg-rose-500/10">
            <RefreshCw size={12} /> Erneut laden
          </button>
        </div>
      ) : (
        <SettingsSection<Prefs>
          id="benachrichtigungen:ruhezeit-kanaele"
          title="Ruhezeit und Kanäle"
          description="In der Ruhezeit werden Push-Nachrichten mit Priorität „normal“ oder „niedrig“ gesammelt und erst danach zugestellt. Dringende Nachrichten kommen sofort."
          baseline={prefs}
          testAnchor="ruhezeit-kanaele"
          validate={(v) => ((v.quietStart && !v.quietEnd) || (!v.quietStart && v.quietEnd)) ? ['Ruhezeit: Beginn und Ende gemeinsam angeben oder beide leer lassen.'] : []}
          onSave={save}
          renderRead={(v) => <PrefsRead value={v} meta={meta} />}
          renderEdit={(v, setField) => <PrefsEdit value={v} setField={setField} meta={meta} />}
        />
      )}
    </div>
  );
}

function effectiveChannels(v: Prefs, meta: Meta, slug: string): Channel[] {
  const ov = v.overrides[slug];
  if (ov) return ov;
  return meta.defaults[slug] ?? ['in_app'];
}

function PrefsRead({ value: v, meta }: { value: Prefs; meta: Meta }) {
  const slugList = useMemo(() => Object.keys(meta.defaults).sort(), [meta.defaults]);
  return (
    <div className="space-y-4 text-sm">
      <div>
        <div className="text-xs text-muted-foreground mb-1">Ruhezeit</div>
        <div>
          {v.quietStart && v.quietEnd ? `${v.quietStart} bis ${v.quietEnd} Uhr` : 'Keine Ruhezeit'}
          {v.weekends && ' · Wochenende'}
          {v.holidays && ' · Feiertage'}
        </div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-1">Kanäle pro Ereignis</div>
        {slugList.length === 0 ? (
          <div className="text-muted-foreground">Keine Ereignis-Typen bekannt.</div>
        ) : (
          <ul className="divide-y divide-default border border-default rounded-md">
            {slugList.map((slug) => {
              const current = effectiveChannels(v, meta, slug);
              const shown = VISIBLE_CHANNELS.filter((ch) => current.includes(ch)).map((ch) => CHANNEL_LABELS[ch]);
              return (
                <li key={slug} className="flex items-center justify-between gap-2 px-3 py-1.5">
                  <span className="min-w-0">
                    <span className="block truncate">{meta.labels[slug] ?? slug}</span>
                    {slug in v.overrides && <span className="text-[10px] text-muted-foreground">persönlich angepasst</span>}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{shown.length ? shown.join(', ') : 'aus'}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function PrefsEdit({ value: v, setField, meta }: { value: Prefs; setField: <K extends keyof Prefs>(key: K, val: Prefs[K]) => void; meta: Meta }) {
  const slugList = useMemo(() => Object.keys(meta.defaults).sort(), [meta.defaults]);

  const isMandatoryLocked = (slug: string, channel: Channel): boolean => {
    if (channel !== 'push_web') return false;
    if (meta.isGfOrAdmin) return false;
    return meta.mandatorySlugs.includes(slug);
  };

  const toggleChannel = (slug: string, channel: Channel): void => {
    // Pflicht-Slugs: push_web darf von normalen Anwendern nicht abgeschaltet werden.
    if (isMandatoryLocked(slug, channel)) return;
    const current = effectiveChannels(v, meta, slug);
    const has = current.includes(channel);
    const next = has ? current.filter((c) => c !== channel) : [...current, channel];
    setField('overrides', { ...v.overrides, [slug]: next });
  };

  const resetSlug = (slug: string): void => {
    const next = { ...v.overrides };
    delete next[slug];
    setField('overrides', next);
  };

  return (
    <div className="space-y-5">
      <section>
        <h3 className="text-sm font-semibold mb-2">Ruhezeit</h3>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Beginn</label>
            <input type="time" value={v.quietStart} onChange={(e) => setField('quietStart', e.target.value)} className="w-full h-10 px-3 rounded-md border border-default bg-background" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Ende</label>
            <input type="time" value={v.quietEnd} onChange={(e) => setField('quietEnd', e.target.value)} className="w-full h-10 px-3 rounded-md border border-default bg-background" />
          </div>
        </div>
        <div className="mt-3 space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={v.weekends} onChange={(e) => setField('weekends', e.target.checked)} className="w-4 h-4" />
            Ganzes Wochenende (Sa+So) als Ruhezeit
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={v.holidays} onChange={(e) => setField('holidays', e.target.checked)} className="w-4 h-4" />
            Feiertage als Ruhezeit
          </label>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold mb-2">Kanäle pro Ereignis</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Standardkanäle sind vorbelegt. Umschalten setzt eine persönliche Anpassung.
          „Standard“ fällt auf die Vorgabe zurück.
        </p>
        {slugList.length === 0 ? (
          <div className="text-sm text-muted-foreground">Keine Ereignis-Typen bekannt.</div>
        ) : (
          <ul className="space-y-2">
            {slugList.map((slug) => {
              const label = meta.labels[slug] ?? slug;
              const current = effectiveChannels(v, meta, slug);
              const isOverridden = slug in v.overrides;
              return (
                <li key={slug} className="border border-default rounded-md p-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="text-sm font-medium">{label}</div>
                      <div className="text-[11px] text-muted-foreground">{slug}</div>
                    </div>
                    {isOverridden && (
                      <button
                        type="button"
                        onClick={() => resetSlug(slug)}
                        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                        title="Auf Standard zurücksetzen"
                      >
                        <RotateCcw size={11} /> Standard
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {VISIBLE_CHANNELS.map((ch) => {
                      const active = current.includes(ch);
                      const locked = isMandatoryLocked(slug, ch);
                      return (
                        <button
                          key={ch}
                          type="button"
                          onClick={() => toggleChannel(slug, ch)}
                          disabled={locked}
                          aria-pressed={active}
                          title={locked ? 'Pflicht durch Admin — kann nicht deaktiviert werden' : undefined}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border ${
                            active
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background border-default text-muted-foreground hover:text-foreground'
                          } ${locked ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                          {locked && <Lock size={10} />}
                          {CHANNEL_LABELS[ch]}
                        </button>
                      );
                    })}
                  </div>
                  {meta.mandatorySlugs.includes(slug) && !meta.isGfOrAdmin && (
                    <div className="mt-1 text-[10px] text-muted-foreground italic">
                      Pflicht-Benachrichtigung — Push kann nicht abgeschaltet werden.
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
